import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { format, addDays, subDays, isToday, parseISO } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import {
  Plus, ChevronLeft, ChevronRight, RefreshCw, Eye, Users, Sparkles, GripVertical,
} from 'lucide-react';

import { useAuth, useTheme } from '../App';
import { PageLayout } from '../components/Sidebar';
import { useOrg } from '../contexts/OrgContext';
import { useWorkAreas } from '../contexts/WorkAreaContext';
import { getEffortBucket } from '../design-system/effortScale';

import {
  OrkaButton,
  OrkaSurface,
  OrkaText,
  OrkaTaskCard,
  OrkaTaskRow,
} from '../components/orka';
import { TodayCommandBand } from '../components/orka/dashboard/TodayCommandBand';
import { FocusStripWidget } from '../components/orka/dashboard/FocusStripWidget';
import DatePickerWithStats from '../components/dashboard/DatePickerWithStats';
import TaskModal from '../components/dashboard/TaskModal';
import CreateTaskModalV2 from '../components/dashboard/CreateTaskModalV2';
import { CreateTaskModalV5, EditTaskModalV5 } from '../components/design-system-v3/TaskFormModalV5';
import EODModal from '../components/dashboard/EODModal';
import GetItDoneModal from '../components/dashboard/GetItDoneModal';
import PartialReviewModal from '../components/dashboard/PartialReviewModal';
import PaneFilterSort, { applyPaneFilterSort } from '../components/dashboard/PaneFilterSort';

import {
  DailyPanesScope,
  DailyDashboardPane,
} from './design-system/DailyPanesShowcase';
import { V3TaskCard, densityForEffort as v3DensityForEffort } from '../components/design-system/OrkaTaskCardV5';
import {
  computeStacking as plannerComputeStacking,
  durationHoursFromTask as plannerDurationHours,
  timeToHours as plannerTimeToHours,
} from '../lib/dashboardPlannerGrid';

/** Measures its container width and passes it as `width` into V3TaskCard so it picks the right responsive mode. */
function ResponsiveV3TaskCard(props) {
  const ref = React.useRef(null);
  const [w, setW] = React.useState(720);
  const [isDark, setIsDark] = React.useState(() => typeof document !== 'undefined' && document.documentElement.classList.contains('dark'));
  React.useEffect(() => {
    if (!ref.current) return;
    const ro = new ResizeObserver((entries) => {
      const ww = entries[0]?.contentRect?.width || 720;
      setW(Math.round(ww));
    });
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);
  React.useEffect(() => {
    const mo = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains('dark'));
    });
    mo.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => mo.disconnect();
  }, []);
  return (
    <div ref={ref} className="orka-v3-scope" data-theme={isDark ? 'dark' : 'light'} style={{ width: '100%', height: '100%' }}>
      <V3TaskCard {...props} width={w} />
    </div>
  );
}

const API = '/api';

/**
 * DailyDashboard2 — fully design-system-driven Daily Dashboard.
 *
 * Layout rules from the latest user spec:
 *   - No top page header. The Daily Command Surface IS the top section.
 *   - All actions (Sync day, day shifter, date picker, New task) live INSIDE
 *     the command surface as `rightSlot`.
 *   - Single-page viewport with internal scrollers — page itself never
 *     scrolls. Each pane scrolls its body.
 *   - Resizable columns: drag splitter between Day Planner (left) and the
 *     stacked right column. Drag splitter between Today · Unplanned (top
 *     right) and Backlog (bottom right) to reweight them.
 *   - Day Planner is a real timeline rail (variant="timeline" of the design
 *     system pane). OrkaTaskCards are positioned by start/end time and sized
 *     by effort.
 *   - Cards inherit their work-area colour via project_color_hex enrichment.
 *   - Theme follows the live app theme (DailyPanesScope mirrors `<html>.dark`).
 */

function parseTimeToHours(t) {
  return plannerTimeToHours(t);
}

function durationHoursFromTask(t) {
  return plannerDurationHours(t);
}

export default function DailyDashboard2() {
  const { user, logout, actingAs } = useAuth();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const { orgId } = useOrg();
  const { workAreaColors, workAreas: rawWorkAreas } = useWorkAreas();

  // V3TaskCard needs a `{work_area_id, label, color, is_personal}` shape.
  const v3WorkAreas = useMemo(() => (rawWorkAreas || []).map(wa => ({
    work_area_id: wa.work_area_id,
    label: wa.name || wa.work_area_id,
    short: (wa.name || '').slice(0, 6).toUpperCase(),
    color: wa.color || '#64748b',
    is_personal: !!wa.is_personal,
  })), [rawWorkAreas]);

  const [tasks, setTasks] = useState([]);
  const [backlogTasks, setBacklogTasks] = useState([]);
  const [supportingTasks, setSupportingTasks] = useState([]);
  const [supportingBacklogTasks, setSupportingBacklogTasks] = useState([]);
  const [supportedUsers, setSupportedUsers] = useState([]);
  const [partialTasks, setPartialTasks] = useState([]);
  const [eodSummary, setEodSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  const [selectedDate, setSelectedDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('mine');
  const [observerOnly, setObserverOnly] = useState(false);

  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showCreateV2, setShowCreateV2] = useState(false);
  const [createV2Defaults, setCreateV2Defaults] = useState(null);
  const [showCreateV3, setShowCreateV3] = useState(false);
  const [createV3Defaults, setCreateV3Defaults] = useState(null);
  const [showEditV3, setShowEditV3] = useState(false);
  const [editV3Task, setEditV3Task] = useState(null);
  const [editingTask, setEditingTask] = useState(null);
  const [taskModalDefaults, setTaskModalDefaults] = useState(null);
  const [showEODModal, setShowEODModal] = useState(false);
  const [showPartialModal, setShowPartialModal] = useState(false);
  // Pane-local filter+sort state — Backlog & Unplanned only (Day Planner
  // sorts by start time which is intrinsic to the timeline rail).
  const [unplannedFS, setUnplannedFS] = useState({ sort: 'priority', dir: 'desc', filter: '' });
  const [backlogFS,   setBacklogFS]   = useState({ sort: 'priority', dir: 'desc', filter: '' });
  const [showGetItDone, setShowGetItDone] = useState(false);
  const [getItDoneTask, setGetItDoneTask] = useState(null);

  const dateStr = format(selectedDate, 'yyyy-MM-dd');

  /* ———————————————————————— Data fetching ———————————————————————— */

  const fetchTasks = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ date: dateStr, include_observed: 'true' });
      if (actingAs) params.append('acting_as', actingAs);
      if (orgId) params.append('org_id', orgId);
      const res = await fetch(`${API}/tasks?${params}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        const userMemId = user.current_membership_id;
        setTasks(data.map(t => ({
          ...t,
          _isObserved: (t.owner_membership_id ? t.owner_membership_id !== userMemId : t.owner_user_id !== user.user_id)
            && ((userMemId && (t.observer_membership_ids || []).includes(userMemId)) || (t.observers || []).includes(user.user_id)),
        })));
      }
    } catch { toast.error('Failed to load tasks'); }
    finally { setLoading(false); }
  }, [user, dateStr, actingAs, orgId]);

  const fetchBacklog = useCallback(async () => {
    if (!user) return;
    try {
      const params = new URLSearchParams({ backlog: 'true', include_observed: 'true' });
      if (actingAs) params.append('acting_as', actingAs);
      if (orgId) params.append('org_id', orgId);
      const res = await fetch(`${API}/tasks?${params}`, { credentials: 'include' });
      if (res.ok) setBacklogTasks(await res.json());
    } catch { /* silent */ }
  }, [user, actingAs, orgId]);

  const fetchSupporting = useCallback(async () => {
    if (!user || viewMode === 'mine') {
      setSupportingTasks([]); setSupportingBacklogTasks([]); setSupportedUsers([]); return;
    }
    try {
      const p1 = new URLSearchParams({ date: dateStr });
      if (orgId) p1.append('org_id', orgId);
      if (observerOnly) p1.append('observer_only', 'true');
      const r1 = await fetch(`${API}/tasks/supporting?${p1}`, { credentials: 'include' });
      if (r1.ok) {
        const data = await r1.json();
        setSupportedUsers(data.supported_users || []);
        setSupportingTasks((data.tasks || []).map(t => ({ ...t, _isSupporting: true })));
      }
      const p2 = new URLSearchParams({ backlog: 'true' });
      if (orgId) p2.append('org_id', orgId);
      if (observerOnly) p2.append('observer_only', 'true');
      const r2 = await fetch(`${API}/tasks/supporting?${p2}`, { credentials: 'include' });
      if (r2.ok) {
        const data = await r2.json();
        setSupportingBacklogTasks((data.tasks || []).map(t => ({ ...t, _isSupporting: true })));
      }
    } catch { /* silent */ }
  }, [user, viewMode, dateStr, observerOnly, orgId]);

  const fetchPartials = useCallback(async () => {
    if (!user) return;
    try {
      const params = new URLSearchParams();
      if (actingAs) params.append('acting_as', actingAs);
      const res = await fetch(`${API}/tasks/partial?${params}`, { credentials: 'include' });
      if (res.ok) setPartialTasks(await res.json());
    } catch { /* silent */ }
  }, [user, actingAs]);

  const fetchEOD = useCallback(async () => {
    if (!user) return;
    try {
      const params = new URLSearchParams({ date: dateStr });
      if (actingAs) params.append('acting_as', actingAs);
      const res = await fetch(`${API}/eod-summaries?${params}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setEodSummary(data.length > 0 ? data[0] : null);
      }
    } catch { /* silent */ }
  }, [user, dateStr, actingAs]);

  useEffect(() => {
    fetchTasks(); fetchBacklog(); fetchPartials(); fetchEOD(); fetchSupporting();
  }, [fetchTasks, fetchBacklog, fetchPartials, fetchEOD, fetchSupporting]);

  /* ———————————————————————— Mutations ———————————————————————— */

  const saveTask = async (taskData) => {
    try {
      const params = new URLSearchParams();
      if (actingAs) params.append('acting_as', actingAs);
      if (orgId) params.append('org_id', orgId);
      const url = editingTask ? `${API}/tasks/${editingTask.task_id}?${params}` : `${API}/tasks?${params}`;
      const res = await fetch(url, {
        method: editingTask ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ ...taskData, task_delivery_date: taskData.task_delivery_date || dateStr }),
      });
      if (res.ok) {
        toast.success(editingTask ? 'Task updated' : 'Task created');
        setShowTaskModal(false); setEditingTask(null);
        fetchTasks(); fetchBacklog();
        return true;
      }
      const err = await res.json().catch(() => ({}));
      toast.error(err?.detail || `Save failed (HTTP ${res.status})`);
      return false;
    } catch { toast.error('Network error'); return false; }
  };

  const doUpdateTask = useCallback(async (taskId, updates) => {
    try {
      const params = new URLSearchParams();
      if (actingAs) params.append('acting_as', actingAs);
      if (orgId) params.append('org_id', orgId);
      const res = await fetch(`${API}/tasks/${taskId}?${params}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(updates),
      });
      if (res.ok) {
        setTasks(prev => prev.map(t => t.task_id === taskId ? { ...t, ...updates } : t));
        setBacklogTasks(prev => prev.map(t => t.task_id === taskId ? { ...t, ...updates } : t));
      }
    } catch { /* silent */ }
  }, [actingAs, orgId]);

  const syncDay = async () => {
    setSyncing(true);
    try {
      const params = new URLSearchParams();
      if (actingAs) params.append('acting_as', actingAs);
      const res = await fetch(`${API}/oauth/calendar/sync-day?${params}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ date: dateStr }),
      });
      if (res.ok) {
        const d = await res.json();
        toast.success(`Synced ${d.synced_count} new · ${d.updated_count} updated`);
        fetchTasks();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err?.detail || 'Sync failed');
      }
    } catch { toast.error('Sync failed'); }
    finally { setSyncing(false); }
  };

  // Edit pencil → V3 Edit Task Modal (full-fields, all editable).
  const openEditTask = useCallback((t) => { setEditV3Task(t); setShowEditV3(true); }, []);
  // "+ New task" button — defaults date to the day-planner's currently
  // displayed day so the user can immediately Save without re-picking it.
  const openNewTask = () => {
    const dStr = selectedDate ? new Date(selectedDate).toISOString().slice(0, 10) : null;
    setCreateV3Defaults(dStr ? { task_delivery_date: dStr } : null);
    setShowCreateV3(true);
  };
  const openNewTaskAt = useCallback((startTime, endTime, effort) => {
    const dStr = selectedDate ? new Date(selectedDate).toISOString().slice(0, 10) : null;
    // V3 Create modal — every field is editable; defaults are SUGGESTIONS,
    // not locked. The user can change date / time / effort / area before save.
    setCreateV3Defaults({
      task_delivery_date: dStr,
      task_start_time: startTime || "",
      task_end_time: endTime || "",
      task_estimated_effort: effort || 3,
    });
    setShowCreateV3(true);
  }, [selectedDate]);

  // V3 modal save handler — POSTs /api/tasks and refreshes panes.
  const saveCreateV3 = useCallback(async (payload) => {
    try {
      const params = new URLSearchParams();
      if (actingAs) params.append('acting_as', actingAs);
      if (orgId) params.append('org_id', orgId);
      const res = await fetch(`${API}/tasks?${params}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err?.detail || `Save failed (HTTP ${res.status})`);
        return false;
      }
      toast.success('Task created');
      fetchTasks(); fetchBacklog();
      return true;
    } catch {
      toast.error('Network error');
      return false;
    }
  }, [actingAs, orgId, fetchTasks, fetchBacklog]);

  // V3 Edit modal — PUTs /api/tasks/:id with the full payload (every field).
  const saveEditV3 = useCallback(async (payload) => {
    if (!editV3Task?.task_id) return false;
    try {
      const params = new URLSearchParams();
      if (actingAs) params.append('acting_as', actingAs);
      if (orgId) params.append('org_id', orgId);
      const res = await fetch(`${API}/tasks/${editV3Task.task_id}?${params}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err?.detail || `Save failed (HTTP ${res.status})`);
        return false;
      }
      toast.success('Task updated');
      fetchTasks(); fetchBacklog();
      return true;
    } catch {
      toast.error('Network error');
      return false;
    }
  }, [actingAs, orgId, fetchTasks, fetchBacklog, editV3Task]);

  // V3 Edit modal — DELETE /api/tasks/:id.
  const deleteEditV3 = useCallback(async (taskId) => {
    try {
      const params = new URLSearchParams();
      if (actingAs) params.append('acting_as', actingAs);
      if (orgId) params.append('org_id', orgId);
      const res = await fetch(`${API}/tasks/${taskId}?${params}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) {
        toast.error(`Delete failed (HTTP ${res.status})`);
        return false;
      }
      toast.success('Task deleted');
      fetchTasks(); fetchBacklog();
      return true;
    } catch {
      toast.error('Network error');
      return false;
    }
  }, [actingAs, orgId, fetchTasks, fetchBacklog]);

  // V2 modal save handler — POSTs to /api/tasks and refreshes panes.
  // Kept for one-off legacy callers (eg AI suggestions). New flows use V3.
  const saveCreateV2 = useCallback(async (payload) => {
    try {
      const params = new URLSearchParams();
      if (actingAs) params.append('acting_as', actingAs);
      if (orgId) params.append('org_id', orgId);
      const res = await fetch(`${API}/tasks?${params}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err?.detail || `Save failed (HTTP ${res.status})`);
        return false;
      }
      toast.success('Task created');
      fetchTasks(); fetchBacklog();
      return true;
    } catch {
      toast.error('Network error');
      return false;
    }
  }, [actingAs, orgId, fetchTasks, fetchBacklog]);
  const startGetItDone = (t) => { setGetItDoneTask(t); setShowGetItDone(true); };

  /* External-drag state for rail ghost preview + drop handler */
  const [draggingTask, setDraggingTask] = useState(null);
  const beginExternalDrag = useCallback((t) => setDraggingTask(t), []);
  const endExternalDrag = useCallback(() => setDraggingTask(null), []);

  // Drag-image size = effort-based proportional rail height (120 px / hr).
  // Creates a transparent preview ghost the size the task occupies on the
  // planner rail; promotes the user's mental model that "dragging this card
  // means dropping a 60-min block / 3-hour block / etc." Auto-cleanup.
  const setEffortDragImage = useCallback((e, t) => {
    const eff = t?.task_estimated_minutes ? Number(t.task_estimated_minutes) : (t?.task_estimated_effort || 3);
    const minutes = t?.task_estimated_minutes
      ? Number(t.task_estimated_minutes)
      : ({ 1: 15, 2: 30, 3: 60, 5: 180, 8: 360, 13: 600 }[eff] || 60);
    const px = Math.max(30, Math.round((minutes / 60) * 120));
    const ghost = document.createElement('div');
    ghost.setAttribute('aria-hidden', 'true');
    ghost.style.cssText = [
      'position:fixed', 'top:-9999px', 'left:-9999px',
      'width:280px', `height:${px}px`,
      'border-radius:14px',
      'border:2px dashed rgba(15,23,42,.35)',
      'background:linear-gradient(180deg,rgba(15,23,42,.08),rgba(15,23,42,.04))',
      'box-shadow:0 18px 36px rgba(15,23,42,.18)',
      'pointer-events:none', 'z-index:99999',
      'display:flex', 'align-items:center', 'justify-content:center',
      'color:rgba(15,23,42,.62)', 'font:900 12px Inter,system-ui,sans-serif',
      'letter-spacing:-.01em',
    ].join(';');
    ghost.textContent = `${minutes}m · ${(t?.task_title || '').slice(0, 32) || 'Task'}`;
    document.body.appendChild(ghost);
    try { e.dataTransfer.setDragImage(ghost, 140, Math.min(px / 2, 80)); } catch {}
    setTimeout(() => { try { document.body.removeChild(ghost); } catch {} }, 0);
  }, []);
  const handleTimelineDrop = useCallback(async (task, newStart, newEnd) => {
    if (!task || !task.task_id) return;
    try {
      // Apply both date (in case it came from backlog) and time.
      await doUpdateTask(task.task_id, {
        task_delivery_date: dateStr,
        task_start_time: newStart,
        task_end_time: newEnd,
      });
      toast.success('Moved on planner');
    } finally {
      setDraggingTask(null);
    }
  }, [doUpdateTask, dateStr]);

  // Drop on Unplanned pane: clear start/end time but keep date set to current
  // pane's day. Works whether source is the planner (already had this date)
  // or the backlog (date was null and now becomes today's pane date).
  const handleUnplannedDrop = useCallback(async (e) => {
    e.preventDefault();
    if (!draggingTask) return;
    try {
      await doUpdateTask(draggingTask.task_id, {
        task_delivery_date: dateStr,
        task_start_time: null,
        task_end_time: null,
      });
      toast.success('Moved to Unplanned');
      fetchTasks(); fetchBacklog();
    } finally { setDraggingTask(null); }
  }, [draggingTask, doUpdateTask, dateStr, fetchTasks, fetchBacklog]);

  // Drop on Backlog pane: clear date AND time entirely.
  const handleBacklogDrop = useCallback(async (e) => {
    e.preventDefault();
    if (!draggingTask) return;
    try {
      await doUpdateTask(draggingTask.task_id, {
        task_delivery_date: null,
        task_start_time: null,
        task_end_time: null,
      });
      toast.success('Moved to Backlog');
      fetchTasks(); fetchBacklog();
    } finally { setDraggingTask(null); }
  }, [draggingTask, doUpdateTask, fetchTasks, fetchBacklog]);

  const onPaneDragOver = useCallback((e) => {
    if (!draggingTask) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, [draggingTask]);

  /* ———————————————————————— Derived data ———————————————————————— */

  const enrich = useCallback((t) => {
    if (!t) return t;
    const colorByName = workAreaColors?.[t.work_area_name];
    const colorById = workAreaColors?.[t.work_area_id];
    const project_color_hex = t.project_color_hex || colorByName || colorById;
    return project_color_hex ? { ...t, project_color_hex } : t;
  }, [workAreaColors]);

  const merged = useMemo(() => {
    if (viewMode === 'mine') return { tasks: tasks.map(enrich), backlogTasks: backlogTasks.map(enrich) };
    if (viewMode === 'supporting') return { tasks: supportingTasks.map(enrich), backlogTasks: supportingBacklogTasks.map(enrich) };
    const dedup = (arr) => { const m = new Map(); arr.forEach(t => m.set(t.task_id, t)); return [...m.values()]; };
    return {
      tasks: dedup([...tasks, ...supportingTasks]).map(enrich),
      backlogTasks: dedup([...backlogTasks, ...supportingBacklogTasks]).map(enrich),
    };
  }, [viewMode, tasks, backlogTasks, supportingTasks, supportingBacklogTasks, enrich]);

  const plannedTasks = useMemo(
    () => (merged.tasks || [])
      .filter(t => t.task_start_time)
      .sort((a, b) => (a.task_start_time || '').localeCompare(b.task_start_time || '')),
    [merged.tasks],
  );
  // Unplanned tasks must be on TODAY's pane and have no start time. A task
  // with no delivery date has been moved to the Backlog and must NOT appear
  // in Unplanned anymore (Feb 2026 fix — drag-to-Backlog used to leave the
  // task visible in Unplanned because the optimistic update kept it in the
  // `tasks` list with both date+time cleared).
  const unplannedTasks = useMemo(
    () => (merged.tasks || []).filter(t => !t.task_start_time && (t.task_delivery_date || t.task_local_date)),
    [merged.tasks],
  );

  const plannedEffort = useMemo(() => plannedTasks.reduce((s, t) => s + (t.task_estimated_effort || 0), 0), [plannedTasks]);
  const unplannedEffort = useMemo(() => unplannedTasks.reduce((s, t) => s + (t.task_estimated_effort || 0), 0), [unplannedTasks]);
  const backlogEffort = useMemo(() => (merged.backlogTasks || []).reduce((s, t) => s + (t.task_estimated_effort || 0), 0), [merged.backlogTasks]);
  const totalEffort = plannedEffort + unplannedEffort;
  const plannedMinutes = useMemo(() => plannedTasks.reduce((s, t) => s + (t.task_estimated_minutes || 0), 0), [plannedTasks]);

  const focusTask = useMemo(
    () => plannedTasks.find(t => t.task_status === 'In Progress')
      || plannedTasks.find(t => ['To Do', 'Blocked'].includes(t.task_status) && (t.task_priority ?? 3) <= 1)
      || plannedTasks[0] || unplannedTasks[0] || merged.backlogTasks[0] || null,
    [plannedTasks, unplannedTasks, merged.backlogTasks],
  );

  const plannerStacks = useMemo(() => {
    // key → stackLevel lookup
    const out = {};
    plannerComputeStacking(plannedTasks).forEach((s) => {
      out[s.task.task_id] = s.stackLevel;
    });
    return out;
  }, [plannedTasks]);

  const timelineItems = useMemo(() => plannedTasks.map((t) => {
    const startHour = parseTimeToHours(t.task_start_time);
    if (startHour == null) return null;
    return {
      key: t.task_id,
      startHour,
      durationHours: durationHoursFromTask(t),
      stackLevel: plannerStacks[t.task_id] || 0,
      content: (
        <div
          draggable
          onDragStart={(e) => { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', t.task_id); setEffortDragImage(e, t); beginExternalDrag(t); }}
          onDragEnd={endExternalDrag}
          data-testid={`dd2-planner-drag-${t.task_id}`}
          style={{ cursor: 'grab' }}
        >
          <ResponsiveV3TaskCard
            task={t}
            density={v3DensityForEffort(t.task_estimated_minutes ? Number(t.task_estimated_minutes) : t.task_estimated_effort, !!t.task_estimated_minutes)}
            workAreas={v3WorkAreas}
            personalSubAreas={[]}
            onChange={(updates) => doUpdateTask(t.task_id, updates)}
            onEdit={openEditTask}
            onEditLink="#"
            data-testid={`dd2-task-${t.task_id}`}
          />
        </div>
      ),
    };
  }).filter(Boolean), [plannedTasks, v3WorkAreas, plannerStacks, doUpdateTask]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ———————————————————————— Render ———————————————————————— */

  const nowTz = useMemo(() => {
    try {
      const parts = new Intl.DateTimeFormat('en-US', { timeZoneName: 'short' }).formatToParts(new Date());
      const tz = parts.find(p => p.type === 'timeZoneName');
      return tz?.value || Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    } catch {
      return '';
    }
  }, []);

  // Urgency border (inherited from Dashboard 1) — colours the planner pane
  // border when there are still incomplete tasks for the day.
  const plannerBorderTone = useMemo(() => {
    const today = format(new Date(), 'yyyy-MM-dd');
    const hour = new Date().getHours();
    const incomplete = [...plannedTasks, ...unplannedTasks].some(t => ['To Do', 'In Progress', 'Blocked'].includes(t.task_status));
    if (dateStr > today) return undefined;
    if (dateStr < today) return incomplete ? 'red' : undefined;
    if (!incomplete) return undefined;
    if (hour < 13) return 'green';
    if (hour <= 16) return 'blue';
    if (hour <= 19) return 'amber';
    return 'red';
  }, [dateStr, plannedTasks, unplannedTasks]);

  const backlogBorderTone = useMemo(
    () => (merged.backlogTasks || []).some(t => ['To Do', 'In Progress', 'Blocked'].includes(t.task_status)) ? 'amber' : undefined,
    [merged.backlogTasks],
  );

  const unplannedBorderTone = useMemo(
    () => unplannedTasks.length > 0 ? 'amber' : undefined,
    [unplannedTasks],
  );

  // LEFT-SLOT actions (under the date headline, inside the command surface).
  const commandLeftStack = (
    <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="dd2-command-left-actions">
      {/* Sync day */}
      <OrkaButton
        variant="ghost"
        size="sm"
        icon={RefreshCw}
        loading={syncing}
        onClick={syncDay}
        data-testid="dd2-sync-day"
      >
        Sync day
      </OrkaButton>
      {/* Day shifter */}
      <div className="inline-flex items-center orka-surface-raised rounded-lg border orka-border-subtle">
        <OrkaButton variant="ghost" size="icon" icon={ChevronLeft} onClick={() => setSelectedDate(d => subDays(d, 1))} data-testid="dd2-prev-day" />
        <button
          type="button"
          className="px-3 h-8 text-sm font-medium orka-text-primary"
          onClick={() => setSelectedDate(new Date())}
          data-testid="dd2-today"
        >
          Today
        </button>
        <OrkaButton variant="ghost" size="icon" icon={ChevronRight} onClick={() => setSelectedDate(d => addDays(d, 1))} data-testid="dd2-next-day" />
      </div>
      {/* Date picker */}
      <DatePickerWithStats
        value={dateStr}
        onChange={(v) => v && setSelectedDate(parseISO(v))}
        triggerClassName="h-9 rounded-lg border orka-border-subtle orka-surface-raised font-mono w-40"
      />
    </div>
  );

  // RIGHT-SLOT for Day Planner pane — view toggle + new task + plus.
  const plannerRightSlot = (
    <div className="flex items-center gap-2 flex-wrap" data-testid="dd2-planner-right-actions">
      <ViewModeToggle viewMode={viewMode} setViewMode={setViewMode} />
      {viewMode !== 'mine' && (
        <button
          type="button"
          onClick={() => setObserverOnly(v => !v)}
          className={`text-[11px] font-bold px-2 h-7 rounded-md inline-flex items-center gap-1 ${
            observerOnly ? 'orka-bg-intent-soft orka-text-intent' : 'orka-text-tertiary hover:orka-text-secondary'
          }`}
          data-testid="dd2-observer-only"
        >
          <Eye className="w-3 h-3" /> Observer
        </button>
      )}
      <OrkaButton variant="primary" size="sm" icon={Plus} onClick={openNewTask} data-testid="dd2-new-task">
        New task
      </OrkaButton>
    </div>
  );

  return (
    <PageLayout hideHeader chromeless user={user} onLogout={logout} theme={theme} setTheme={setTheme}>
      <div
        className="p-4 h-screen overflow-hidden gap-3"
        style={{ display: 'grid', gridTemplateRows: 'auto auto minmax(0, 1fr)' }}
        data-testid="daily-dashboard-2"
      >
        {/* ————— Daily Command Surface — sole top section ————— */}
        <TodayCommandBand
          date={format(selectedDate, 'EEEE, MMMM d')}
          eyebrow="Daily command surface"
          title={isToday(selectedDate) ? 'Today' : format(selectedDate, 'EEEE')}
          metricItems={(() => {
            const effortBucket = getEffortBucket(totalEffort || 0);
            const overloaded = effortBucket.label === 'Overload' || effortBucket.label === 'Heavy';
            return [
              { label: 'Effort',    value: totalEffort,                          sub: effortBucket.label !== 'Empty' ? effortBucket.label : undefined, boxColor: overloaded ? effortBucket.color : undefined, color: effortBucket.color, testId: 'dd2-metric-effort' },
              { label: 'Planned',   value: plannedTasks.length,                  testId: 'dd2-metric-planned' },
              { label: 'Unplanned', value: unplannedTasks.length,                color: unplannedTasks.length > 0 ? 'var(--orka-text-warning)' : undefined, testId: 'dd2-metric-unplanned' },
              { label: 'Backlog',   value: (merged.backlogTasks || []).length,   testId: 'dd2-metric-backlog' },
              { label: 'Risk',      value: partialTasks.length + plannedTasks.filter(t => t.task_status === 'Blocked').length, color: (partialTasks.length || plannedTasks.some(t => t.task_status === 'Blocked')) ? 'var(--orka-text-danger)' : undefined, testId: 'dd2-metric-risk' },
            ];
          })()}
          leftSlot={commandLeftStack}
          rightSlot={
            viewMode !== 'mine' && supportedUsers.length > 0 ? (
              <div className="flex items-center gap-2 text-xs orka-text-tertiary" data-testid="dd2-supporting-legend">
                <span className="inline-block w-3 h-3 rounded-sm border-2 border-orange-400 bg-orange-50" />
                <span>{supportedUsers.map(u => u.name || u.email).join(', ')}&rsquo;s tasks</span>
              </div>
            ) : null
          }
        />

        {/* ————— Focus strip (Now · Recovery·Signals · Report·End Day) ————— */}
        <FocusStripWidget
          testId="dd2-focus-strip"
          focusTask={focusTask}
          counts={{
            partial: partialTasks.length,
            blocked: plannedTasks.filter(t => t.task_status === 'Blocked').length,
          }}
          partialTasks={partialTasks}
          eodSummary={eodSummary}
          partialCount={partialTasks.length}
          onStartFocus={startGetItDone}
          onOpenTask={openEditTask}
          onStartEOD={() => setShowEODModal(true)}
          onOpenEOD={() => toast.info('EOD already filed for this day')}
          onOpenDoneReport={() => toast.info('Done Report · coming soon in v2')}
          onOpenPartials={() => setShowPartialModal(true)}
          onOpenBlocked={() => setShowPartialModal(true)}
          onRecoverTask={(t) => t && openEditTask(t)}
        />

        {/* ————— Resizable panes ————— */}
        <DailyPanesScope>
          <div className="dp-dashboard min-h-0 overflow-hidden p-2" style={{ height: '100%' }} data-testid="dd2-panes">
            <PanelGroup direction="horizontal" autoSaveId="dd2-h-split" className="h-full">
              <Panel defaultSize={58} minSize={35} className="min-w-0">
                <DailyDashboardPane
                  type="planner"
                  variant="timeline"
                  /* No `timeline` prop → pane uses canonical 04-24 / 120 px/h grid
                     (spec §1 + shared `lib/dashboardPlannerGrid.js`). */
                  timelineItems={timelineItems}
                  nowHour={isToday(selectedDate) ? new Date().getHours() + new Date().getMinutes() / 60 : null}
                  tzLabel={nowTz}
                  borderTone={plannerBorderTone}
                  count={plannedTasks.length}
                  effort={plannedEffort}
                  state={loading ? 'loading' : (plannedTasks.length === 0 ? 'empty' : 'loaded')}
                  onAdd={openNewTask}
                  onCreateTask={openNewTaskAt}
                  onDragDropChange={handleTimelineDrop}
                  draggingTask={draggingTask}
                  onDragEnd={endExternalDrag}
                  rightSlot={plannerRightSlot}
                  testId="dd2-planner"
                />
              </Panel>
              <PanelResizeHandle className="dd2-resizer dd2-resizer--h" data-testid="dd2-resize-h">
                <GripVertical className="w-3 h-3 orka-text-tertiary" />
              </PanelResizeHandle>
              <Panel defaultSize={42} minSize={25} className="min-w-0">
                <PanelGroup direction="vertical" autoSaveId="dd2-v-split" className="h-full">
                  <Panel defaultSize={50} minSize={20} className="min-h-0">
                    <DailyDashboardPane
                      type="unplanned"
                      borderTone={unplannedTasks.length > 0 ? plannerBorderTone : undefined}
                      count={unplannedTasks.length}
                      effort={unplannedEffort}
                      state={loading ? 'loading' : (unplannedTasks.length === 0 ? 'empty' : 'loaded')}
                      rightSlot={(
                        <PaneFilterSort
                          value={unplannedFS}
                          onChange={setUnplannedFS}
                          workAreas={v3WorkAreas}
                          testId="dd2-unplanned-filtersort"
                        />
                      )}
                      acceptsDrop
                      onDragOver={onPaneDragOver}
                      onDrop={handleUnplannedDrop}
                      testId="dd2-pane-unplanned"
                    >
                      {applyPaneFilterSort(unplannedTasks, unplannedFS).map(t => (
                        <div
                          key={t.task_id}
                          data-testid={`dd2-unplanned-drag-${t.task_id}`}
                          draggable
                          onDragStart={(e) => { e.dataTransfer.effectAllowed = 'move'; setEffortDragImage(e, t); beginExternalDrag(t); }}
                          onDragEnd={endExternalDrag}
                        >
                          <ResponsiveV3TaskCard
                            task={t}
                            density={v3DensityForEffort(t.task_estimated_minutes ? Number(t.task_estimated_minutes) : t.task_estimated_effort, !!t.task_estimated_minutes)}
                            workAreas={v3WorkAreas}
                            personalSubAreas={[]}
                            onChange={(updates) => doUpdateTask(t.task_id, updates)}
                            onEdit={openEditTask}
                            onEditLink="#"
                            data-testid={`dd2-task-${t.task_id}`}
                          />
                        </div>
                      ))}
                    </DailyDashboardPane>
                  </Panel>
                  <PanelResizeHandle className="dd2-resizer dd2-resizer--v" data-testid="dd2-resize-v" />
                  <Panel defaultSize={50} minSize={20} className="min-h-0">
                    <DailyDashboardPane
                      type="backlog"
                      count={(merged.backlogTasks || []).length}
                      effort={backlogEffort}
                      state={loading ? 'loading' : ((merged.backlogTasks || []).length === 0 ? 'empty' : 'loaded')}
                      rightSlot={(
                        <PaneFilterSort
                          value={backlogFS}
                          onChange={setBacklogFS}
                          workAreas={v3WorkAreas}
                          testId="dd2-backlog-filtersort"
                        />
                      )}
                      acceptsDrop
                      onDragOver={onPaneDragOver}
                      onDrop={handleBacklogDrop}
                      testId="dd2-pane-backlog"
                    >
                      {applyPaneFilterSort(merged.backlogTasks || [], backlogFS).map(t => (
                        <div
                          key={t.task_id}
                          data-testid={`dd2-backlog-drag-${t.task_id}`}
                          draggable
                          onDragStart={(e) => { e.dataTransfer.effectAllowed = 'move'; setEffortDragImage(e, t); beginExternalDrag(t); }}
                          onDragEnd={endExternalDrag}
                        >
                          <OrkaTaskRow
                            task={t}
                            density="compact"
                            onClick={() => openEditTask(t)}
                            onMenu={() => openEditTask(t)}
                            testId={`dd2-task-row-${t.task_id}`}
                          />
                        </div>
                      ))}
                    </DailyDashboardPane>
                  </Panel>
                </PanelGroup>
              </Panel>
            </PanelGroup>
          </div>
        </DailyPanesScope>
      </div>

      {/* ————— Modals ————— */}
      {showTaskModal && (
        <TaskModal
          open={showTaskModal}
          onOpenChange={(v) => { setShowTaskModal(v); if (!v) { setEditingTask(null); setTaskModalDefaults(null); } }}
          task={editingTask}
          defaultValues={taskModalDefaults}
          onSave={saveTask}
          selectedDate={selectedDate}
        />
      )}
      <CreateTaskModalV2
        open={showCreateV2}
        onOpenChange={(v) => { setShowCreateV2(v); if (!v) setCreateV2Defaults(null); }}
        workAreas={v3WorkAreas}
        defaultValues={createV2Defaults}
        onSave={saveCreateV2}
      />
      <CreateTaskModalV5
        open={showCreateV3}
        onOpenChange={(v) => { setShowCreateV3(v); if (!v) setCreateV3Defaults(null); }}
        workAreas={v3WorkAreas}
        defaultValues={createV3Defaults}
        onSave={saveCreateV3}
      />
      <EditTaskModalV5
        open={showEditV3}
        onOpenChange={(v) => { setShowEditV3(v); if (!v) setEditV3Task(null); }}
        workAreas={v3WorkAreas}
        task={editV3Task}
        onSave={saveEditV3}
        onDelete={deleteEditV3}
      />
      {showEODModal && (
        <EODModal
          open={showEODModal}
          onOpenChange={setShowEODModal}
          selectedDate={selectedDate}
          actingAs={actingAs}
          onComplete={() => { fetchEOD(); setShowEODModal(false); }}
          onViewReport={() => toast.info('EOD report view · coming soon in v2')}
        />
      )}
      <GetItDoneModal
        open={showGetItDone}
        onOpenChange={setShowGetItDone}
        task={getItDoneTask}
      />
      <PartialReviewModal
        open={showPartialModal}
        onOpenChange={setShowPartialModal}
        partialTasks={partialTasks}
        plannedTasks={plannedTasks}
        onOpenTask={openEditTask}
      />

      {/* Resizer handle styles — scoped here so DailyPanes design-system tokens stay clean. */}
      <style>{`
        .dd2-resizer{display:flex;align-items:center;justify-content:center;background:transparent;outline:none}
        .dd2-resizer--h{width:8px;cursor:col-resize}
        .dd2-resizer--v{height:8px;cursor:row-resize}
        .dd2-resizer:hover,.dd2-resizer[data-resize-handle-state="hover"],.dd2-resizer[data-resize-handle-state="drag"]{background:color-mix(in srgb,var(--orka-text-intent,#4f46e5) 35%,transparent)}
        .dd2-resizer--h::after{content:"";display:block;width:2px;height:32px;border-radius:999px;background:var(--orka-border-default,rgba(120,120,120,.3))}
        .dd2-resizer--v::after{content:"";display:block;height:2px;width:32px;border-radius:999px;background:var(--orka-border-default,rgba(120,120,120,.3))}
        .dd2-resizer:hover::after,.dd2-resizer[data-resize-handle-state="hover"]::after,.dd2-resizer[data-resize-handle-state="drag"]::after{background:var(--orka-text-intent,#4f46e5)}
      `}</style>
    </PageLayout>
  );
}

/* ————————————————————————— Subcomponents ————————————————————————— */

function ViewModeToggle({ viewMode, setViewMode }) {
  const btn = (key, label, Icon) => (
    <button
      key={key}
      type="button"
      onClick={() => setViewMode(key)}
      className={`px-3 py-1.5 rounded-md text-xs font-medium orka-transition-colors inline-flex items-center gap-1 ${
        viewMode === key ? 'orka-surface-base shadow-sm orka-text-primary' : 'orka-text-tertiary hover:orka-text-secondary'
      }`}
      data-testid={`dd2-view-${key}`}
    >
      {Icon && <Icon className="w-3.5 h-3.5" />}
      {label}
    </button>
  );
  return (
    <div className="flex items-center orka-surface-subtle rounded-lg p-0.5 gap-0.5" data-testid="dd2-view-toggle">
      {btn('mine', 'Responsible')}
      {btn('supporting', 'Supporting', Users)}
      {btn('both', 'Both')}
    </div>
  );
}
